import React from 'react';
import { Box, Card, Typography, useTheme } from '@mui/material';
import { ArrowUp, ArrowDown, Minus } from './Icons';

const ICON_TINTS = {
  primary:   { bg: 'rgba(29, 182, 131, 0.14)', fg: '#0F5B45' },
  secondary: { bg: 'rgba(245, 158, 11, 0.14)', fg: '#B45309' },
  info:      { bg: 'rgba(59, 130, 246, 0.14)', fg: '#1D4ED8' },
  success:   { bg: 'rgba(16, 185, 129, 0.14)', fg: '#047857' },
  danger:    { bg: 'rgba(239, 68, 68, 0.14)',  fg: '#B91C1C' },
};

/**
 * Dashboard KPI card. Renders icon-in-tinted-circle + label + value +
 * optional delta chip. Used in admin / manager dashboards.
 */
const KpiCard = ({
  icon,
  label,
  value,
  helper,
  delta,
  deltaTone,   // 'up' | 'down' | 'flat' — overrides auto-detect
  comparison,  // small text shown next to the delta, e.g. "vs last month"
  iconTone = 'primary',
}) => {
  const theme = useTheme();
  const tint = ICON_TINTS[iconTone] || ICON_TINTS.primary;
  const statusPalette = theme.palette.mesoon?.status || {};

  // Auto-detect tone from delta string ("+12%" → up, "-3%" → down)
  let tone = deltaTone;
  if (!tone && typeof delta === 'string') {
    const trimmed = delta.trim();
    if (trimmed.startsWith('+')) tone = 'up';
    else if (trimmed.startsWith('-') || trimmed.startsWith('−')) tone = 'down';
    else tone = 'flat';
  }

  const deltaPalette = tone === 'up'
    ? statusPalette.active || { bg: '#D1FAE5', text: '#047857' }
    : tone === 'down'
    ? statusPalette.danger || { bg: '#FEE2E2', text: '#B91C1C' }
    : statusPalette.neutral || { bg: '#F3F4F6', text: '#4B5563' };

  const DeltaIcon = tone === 'up' ? ArrowUp : tone === 'down' ? ArrowDown : Minus;

  return (
    <Card sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        {icon && (
          <Box
            aria-hidden="true"
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: tint.bg,
              color: tint.fg,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        )}
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', fontWeight: 500, textAlign: 'end', flex: 1, minWidth: 0 }}
        >
          {label}
        </Typography>
      </Box>

      <Box>
        <Typography
          component="div"
          sx={{
            fontFamily: '"Plus Jakarta Sans", "IBM Plex Sans Arabic", Inter, sans-serif',
            fontSize: { xs: 28, md: 32 },
            fontWeight: 700,
            color: 'text.primary',
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
          }}
        >
          {value}
        </Typography>
        {helper && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {helper}
          </Typography>
        )}
      </Box>

      {delta != null && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 'auto' }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              padding: '4px 8px',
              borderRadius: 9999,
              backgroundColor: deltaPalette.bg,
              color: deltaPalette.text,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            <DeltaIcon size={14} />
            {delta}
          </Box>
          {comparison && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {comparison}
            </Typography>
          )}
        </Box>
      )}
    </Card>
  );
};

export default KpiCard;
